import { AppConsent } from "components/AppConsent";
import { CONSENT_KEY, INDEX_DB, MANDATORY_CONSENT } from "constants/general";
import { CONSENT_LAYOUT_VARIANTS } from "constants/components";
import {
  getItemWithExpiration,
  setItemWithExpiration,
} from "core/utils/utils.helper";
import useAuth from "hooks/useAuth";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useUserInfoStore } from "@/stores/userInfoStore";
import {
  getUserSpecificConsent,
  saveUserSpecificConsent,
} from "services/consent.service";
import { saveConditionsAccept } from "services/ourConditions.service";
import {
  iDbAddRecord,
  iDbGetAllRecords,
  iDbUpsertRecord,
} from "@/index-db/db.helpers";
import type { UserConsentDataType } from "@/index-db/db.types";
import styles from "./ConsentLayout.module.scss";

// Define consent layout variants type
export type ConsentLayoutVariant =
  (typeof CONSENT_LAYOUT_VARIANTS)[keyof typeof CONSENT_LAYOUT_VARIANTS];

// Interface for the props passed to the ConsentLayout component
export interface ConsentLayoutProps {
  children: ReactNode;
  isShownBeforeAuth?: Boolean;
  variant?: ConsentLayoutVariant;
  className?: string;
}

// ConsentLayout component, responsible for handling consent logic
export const ConsentLayout: React.FC<ConsentLayoutProps> = ({
  children,
  isShownBeforeAuth = false,
  variant = CONSENT_LAYOUT_VARIANTS.DEFAULT,
  className,
}) => {
  // State variables for visibility and loading status
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Get logged in user data for our conditions consent
  const loggedInUserData = useUserInfoStore((s) => s.userInfo);

  // Accessing authentication context to get the current authentication status
  const auth: any = useAuth();
  const isLoggedIn: boolean = auth && auth.isAuthenticated; // Check if the user is logged in

  // Set expiration time (24 hour) for consent data in local storage
  const oneDayInMilliseconds = 24 * 60 * 60 * 1000; // 24 hour

  // Function to fetch the user's specific consent status from the server
  const getUserSpecificConsentResponse = useCallback(async () => {
    setLoading(true); // Set loading to true when fetching data
    setIsVisible(true); // Show consent modal

    try {
      const response = await getUserSpecificConsent();
      if (response && response.status === 200) {
        // Filter out consent data where the consentId matches the mandatory consent and status is 'ACTIVE'
        const checkConsent =
          response?.data?.data &&
          Array.isArray(response?.data?.data) &&
          response?.data?.data?.filter(
            (item: any) =>
              item.consentId === MANDATORY_CONSENT.id &&
              item.status === "ACTIVE",
          );

        // If consent is already given for mandatory consent, hide the consent modal
        if (checkConsent && checkConsent.length !== 0) {
          setIsVisible(false);
          // Save consent approval to local storage with expiration
          setItemWithExpiration(CONSENT_KEY, "true", oneDayInMilliseconds);
        } else {
          // If consent is not given, keep the consent modal visible
          setIsVisible(true);
        }
        setLoading(false); // Set loading to false once data is loaded
      }
    } catch (error) {
      console.log(error); // Log any errors in fetching consent data
    }
  }, [oneDayInMilliseconds]);

  // Function to format consent data to match the expected structure for saving
  const formatResponse = (data: any) => {
    console.log(data); // Logging data for debugging

    return {
      consents: Object.keys(data) // Get keys (consent IDs)
        .filter((key) => data[key].length !== 0) // Only include keys with non-empty values
        .map((key) => ({
          ID: key, // Set the key as the consent ID
          status: "CONFIRMED", // Set the consent status as "CONFIRMED"
        })),
    };
  };

  // Function to submit consent response — receives consent data directly from AppConsent
  const submitConsent = useCallback(
    async (consentData: Record<string, any>) => {
      try {
        // Format the consent data before sending it to the backend
        const responseFormatter = formatResponse(consentData);

        if (isLoggedIn) {
          // If user is logged in, call both APIs together using Promise.all
          const ourConditionsPayload = {
            user_id: loggedInUserData?.accountId || "",
            is_accepted: 1,
          };

          const [ourConditionsResponse, consentResponse] = await Promise.all([
            saveConditionsAccept(ourConditionsPayload),
            saveUserSpecificConsent(responseFormatter),
          ]);

          // Check if both responses are successful
          if (
            ourConditionsResponse?.status === 200 &&
            consentResponse?.status === 200
          ) {
            // Save the consent approval in local storage with expiration
            setItemWithExpiration(CONSENT_KEY, "true", oneDayInMilliseconds);
            setIsVisible(false);
          }
        } else {
          // If user is not logged in, save both consents to IndexedDB together
          const consentDbData: UserConsentDataType = responseFormatter;

          await Promise.all([
            iDbUpsertRecord(INDEX_DB.OUR_CONDITIONS_TABLE, { is_accepted: 1 }),
            iDbAddRecord<UserConsentDataType>(
              INDEX_DB.CONSENTS_TABLE,
              consentDbData,
            ),
          ]);

          setIsVisible(false);
        }
      } catch (error) {
        console.log(error); // Log any errors in submitting the consent
      }
    },
    [isLoggedIn, oneDayInMilliseconds, loggedInUserData],
  );

  // Fetch the user's consent status when the component mounts
  useEffect(() => {
    if (isShownBeforeAuth && !isLoggedIn) {
      setIsVisible(false);
      return;
    }
    const validateConsentVisibility = async () => {
      const retrievedValue: string | null =
        getItemWithExpiration<string>(CONSENT_KEY);
      const userConsentData = await iDbGetAllRecords<UserConsentDataType>(
        INDEX_DB.CONSENTS_TABLE,
      );

      if (!isLoggedIn && !userConsentData.length) {
        setIsVisible(true);
        return;
      }
      // If consent is not already stored in localStorage, fetch user-specific consent
      if (!retrievedValue && isLoggedIn) {
        getUserSpecificConsentResponse();
      }
    };

    validateConsentVisibility();
  }, [isLoggedIn, getUserSpecificConsentResponse]); // Empty dependency array: only runs on component mount

  if (isVisible) {
    // If the user is logged in and the consent modal should be visible, render the modal and the children components

    return (
      <div
        className={`${variant === CONSENT_LAYOUT_VARIANTS.DEFAULT && styles.consent_container} ${className}`}
      >
        <AppConsent
          consentCheckLoading={loading} // Pass loading state to the AppConsent component
          submitConsent={submitConsent} // Pass the submitConsent function to handle form submission
        />
        {children}
      </div>
    );
  } else {
    return <>{children}</>;
  }
};
